import React from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const SECTIONS = [
  { id: "intro", title: "Introduction" },
  { id: "data-we-collect", title: "Data We Collect" },
  { id: "why", title: "Why We Collect It" },
  { id: "legal-basis", title: "Legal Basis for Processing" },
  { id: "storage", title: "Data Storage and Security" },
  { id: "sharing", title: "Data Sharing" },
  { id: "payment", title: "Payment Data" },
  { id: "retention", title: "Data Retention" },
  { id: "rights", title: "Your Rights" },
  { id: "cookies", title: "Cookies" },
  { id: "children", title: "Children's Privacy" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact" },
];

export default function Privacy() {
  return (
    <LegalLayout
      testId="privacy-page"
      title="Privacy Policy"
      lastUpdated="February 3, 2026"
      sections={SECTIONS}
      seoTitle="Privacy Policy | SchengenFile"
      seoDescription="How SchengenFile collects, uses, stores and protects your personal data during Schengen visa document preparation. GDPR-aligned."
    >
      <LegalSection id="intro" title="1. Introduction">
        <p>
          SchengenFile ("we", "us", "our") is a brand operated by Trip360 Travel & Tourism Co.,
          registered in Riyadh, Kingdom of Saudi Arabia. We respect your privacy and are committed
          to protecting the personal data you share with us when you use schengenfile.com and our
          document preparation services (the "Service"). This Privacy Policy explains what data we
          collect, why we collect it, how we use and safeguard it, and the rights you have over it.
        </p>
        <p>
          Because most of our customers apply for visas to European Union member states, we align
          our practices with the principles of the EU General Data Protection Regulation (GDPR),
          alongside applicable data protection laws in the Kingdom of Saudi Arabia.
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title="2. Data We Collect">
        <p>To deliver our Service, we collect only the information required to prepare your documents. Depending on the package or items you order, this may include:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Full name as it appears on your passport;</li>
          <li>Date of birth, nationality, and gender;</li>
          <li>Passport number, place of issue, issue date, and expiry date;</li>
          <li>Email address, WhatsApp number, and mailing address;</li>
          <li>Travel details: destination country, entry and exit airports, dates of travel, hotel city, number of applicants;</li>
          <li>Visa appointment details (consulate, appointment date, application centre);</li>
          <li>Occupation and employer information when required for add-ons such as No-Objection Certificates or sponsor letters;</li>
          <li>Any additional information you voluntarily provide through the dashboard messaging system.</li>
        </ul>
        <p>
          We also collect limited technical data automatically, such as your IP address, browser
          type, device information, and pages visited, in order to operate and secure the Service.
        </p>
      </LegalSection>

      <LegalSection id="why" title="3. Why We Collect It">
        <p>We use your personal data solely for the following purposes:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>To prepare the specific documents you have ordered (flight reservations, hotel bookings, insurance certificates, application forms, cover letters, itineraries, and add-ons);</li>
          <li>To communicate with you about your order, respond to your questions, and provide customer support;</li>
          <li>To process payments and prevent fraud;</li>
          <li>To maintain, secure, and improve the Service;</li>
          <li>To comply with our legal, regulatory, and accounting obligations.</li>
        </ul>
        <p>
          We do <strong>not</strong> use your data for behavioural advertising, and we do{" "}
          <strong>not</strong> sell your personal information to third parties.
        </p>
      </LegalSection>

      <LegalSection id="legal-basis" title="4. Legal Basis for Processing">
        <p>Where GDPR applies, we rely on the following legal bases to process your data:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Performance of a contract</strong> — to deliver the documents and services you have ordered.</li>
          <li><strong>Legal obligation</strong> — to comply with tax, accounting, and other legal requirements.</li>
          <li><strong>Legitimate interests</strong> — to secure our platform, prevent fraud, and improve the Service, provided such interests are not overridden by your rights.</li>
          <li><strong>Consent</strong> — where you have explicitly agreed to a specific use of your data (for example, optional communications).</li>
        </ul>
      </LegalSection>

      <LegalSection id="storage" title="5. Data Storage and Security">
        <p>
          Your personal data is stored in secure databases hosted on reputable cloud infrastructure.
          Access to customer records is restricted to authorised SchengenFile staff who need it to
          fulfil your order or provide support, and is protected by role-based access controls,
          strong password requirements, and audit logging.
        </p>
        <p>
          All data in transit between your browser and our servers is protected by industry-standard
          TLS encryption. Sensitive fields, including passport information, are stored on servers
          with encryption at rest. We regularly review our security practices to ensure they remain
          appropriate to the sensitivity of the data we handle.
        </p>
        <p>
          No system can be guaranteed 100% secure. However, we implement organisational and
          technical measures designed to protect your data against accidental or unlawful
          destruction, loss, alteration, or unauthorised disclosure.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="6. Data Sharing">
        <p>
          We share your personal data only with third parties that are strictly necessary to
          fulfil your order or operate the Service. These may include:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Global Distribution Systems (GDS) and airline booking systems that issue verifiable flight itineraries;</li>
          <li>Hotel providers and hotel booking platforms that issue confirmed accommodation vouchers;</li>
          <li>Licensed travel insurance providers who issue Schengen-compliant certificates;</li>
          <li>Our payment processor for the sole purpose of processing your payment;</li>
          <li>Email and hosting service providers who help us deliver the Service;</li>
          <li>Legal, accounting, or regulatory authorities where we are required by law to disclose information.</li>
        </ul>
        <p>
          These recipients are only permitted to use your data to perform the specific service they
          provide to us and are contractually required to keep your data confidential. We never
          sell, rent, or trade your personal information.
        </p>
      </LegalSection>

      <LegalSection id="payment" title="7. Payment Data">
        <p>
          Card and payment details are entered directly into a checkout hosted by our PCI-DSS
          compliant payment processor. SchengenFile <strong>does not</strong> receive or store your
          full card number, CVV, or bank credentials. We only receive a payment confirmation, a
          truncated reference (e.g. the last four digits of the card), and the transaction status
          needed to reconcile your order.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="8. Data Retention">
        <p>
          We keep your order data (including passport, travel, and contact details) for a period of{" "}
          <strong>24 months</strong> from the date of your order, after which it is deleted or
          irreversibly anonymised. This retention period allows us to provide continuity of service
          for repeat customers, resolve disputes, and comply with our accounting and tax obligations.
        </p>
        <p>
          You may request earlier deletion at any time, subject to any overriding legal obligation
          to retain certain records (for example, tax invoices, which we are required to keep for
          the statutory retention period).
        </p>
      </LegalSection>

      <LegalSection id="rights" title="9. Your Rights">
        <p>You have the following rights in relation to your personal data:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
          <li><strong>Right of rectification</strong> — ask us to correct data that is inaccurate or incomplete.</li>
          <li><strong>Right of erasure</strong> — ask us to delete your data, subject to our legal retention obligations.</li>
          <li><strong>Right to restrict processing</strong> — ask us to limit how we use your data in certain circumstances.</li>
          <li><strong>Right to data portability</strong> — receive your data in a structured, commonly used, machine-readable format.</li>
          <li><strong>Right to object</strong> — object to processing based on our legitimate interests.</li>
          <li><strong>Right to withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time.</li>
          <li><strong>Right to lodge a complaint</strong> — with a competent data protection authority.</li>
        </ul>
        <p>
          To exercise any of these rights, please contact{" "}
          <a href="mailto:support@schengenfile.com" className="text-[#2563eb] hover:underline">
            support@schengenfile.com
          </a>
          . We will respond within 30 days.
        </p>
      </LegalSection>

      <LegalSection id="cookies" title="10. Cookies">
        <p>
          We use only basic functional cookies that are strictly necessary for the operation of the
          Service, such as keeping you signed in to your dashboard and remembering your order in
          progress. We do not use advertising cookies or third-party marketing trackers.
        </p>
        <p>
          You can configure your browser to block or delete cookies, but doing so may prevent
          certain parts of the Service, such as sign-in, from functioning correctly.
        </p>
      </LegalSection>

      <LegalSection id="children" title="11. Children's Privacy">
        <p>
          The Service is not directed to children under 18. Where an application is placed for a
          minor, it must be submitted by a parent or legal guardian, who takes responsibility for
          the accuracy of the minor's data and for these Terms.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="12. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, legal requirements, or other factors. The "Last updated" date at the top of
          this page shows when the policy was most recently revised. Where a change is material, we
          will use reasonable efforts to notify registered users by email or through the dashboard.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="13. Contact">
        <p>
          For privacy questions, data access requests, or to exercise any of your rights, please
          contact us at{" "}
          <a href="mailto:support@schengenfile.com" className="text-[#2563eb] hover:underline">
            support@schengenfile.com
          </a>
          . Postal correspondence may be sent to Trip360 Travel & Tourism Co., Riyadh, Kingdom of
          Saudi Arabia.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
